// Role-based Access Control System
// Defines permissions for different admin roles: admin, marketing, moderator

export const ROLES = {
  ADMIN: 'admin',
  MARKETING: 'marketing',
  MODERATOR: 'moderator'
};

export const PERMISSIONS = {
  // Availability Management
  MANAGE_AVAILABILITY: 'manage_availability',
  VIEW_AVAILABILITY: 'view_availability',

  // Appointment Management
  VIEW_APPOINTMENTS: 'view_appointments',
  MANAGE_APPOINTMENTS: 'manage_appointments',
  DELETE_APPOINTMENTS: 'delete_appointments',

  // Student Data
  VIEW_STUDENTS: 'view_students',
  MANAGE_STUDENTS: 'manage_students',
  DELETE_STUDENTS: 'delete_students',

  // Mailing List
  VIEW_MAILING_LIST: 'view_mailing_list',
  MANAGE_MAILING_LIST: 'manage_mailing_list',
  EXPORT_MAILING_LIST: 'export_mailing_list',

  // Analytics & Reports
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_ANALYTICS: 'export_analytics',

  // Accounting
  VIEW_ACCOUNTING: 'view_accounting',
  MANAGE_PAYMENTS: 'manage_payments',

  // System Settings
  MANAGE_FOMO_SETTINGS: 'manage_fomo_settings',
  VIEW_ACTIVITY_LOGS: 'view_activity_logs',
  MANAGE_SYSTEM_SETTINGS: 'manage_system_settings',

  // User Management (Admin only)
  MANAGE_USERS: 'manage_users',
  MANAGE_ROLES: 'manage_roles',

  // Security
  VIEW_SECURITY_LOGS: 'view_security_logs',
  MANAGE_SECURITY: 'manage_security'
};

// Role permission mapping
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    // Full access to everything
    PERMISSIONS.MANAGE_AVAILABILITY,
    PERMISSIONS.VIEW_AVAILABILITY,
    PERMISSIONS.VIEW_APPOINTMENTS,
    PERMISSIONS.MANAGE_APPOINTMENTS,
    PERMISSIONS.DELETE_APPOINTMENTS,
    PERMISSIONS.VIEW_STUDENTS,
    PERMISSIONS.MANAGE_STUDENTS,
    PERMISSIONS.DELETE_STUDENTS,
    PERMISSIONS.VIEW_MAILING_LIST,
    PERMISSIONS.MANAGE_MAILING_LIST,
    PERMISSIONS.EXPORT_MAILING_LIST,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.EXPORT_ANALYTICS,
    PERMISSIONS.VIEW_ACCOUNTING,
    PERMISSIONS.MANAGE_PAYMENTS,
    PERMISSIONS.MANAGE_FOMO_SETTINGS,
    PERMISSIONS.VIEW_ACTIVITY_LOGS,
    PERMISSIONS.MANAGE_SYSTEM_SETTINGS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_ROLES,
    PERMISSIONS.VIEW_SECURITY_LOGS,
    PERMISSIONS.MANAGE_SECURITY
  ],

  [ROLES.MARKETING]: [
    // Focus on analytics, mailing list, and FOMO settings
    PERMISSIONS.VIEW_AVAILABILITY,
    PERMISSIONS.VIEW_APPOINTMENTS,
    PERMISSIONS.VIEW_STUDENTS,
    PERMISSIONS.VIEW_MAILING_LIST,
    PERMISSIONS.MANAGE_MAILING_LIST,
    PERMISSIONS.EXPORT_MAILING_LIST,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.EXPORT_ANALYTICS,
    PERMISSIONS.MANAGE_FOMO_SETTINGS
  ],

  [ROLES.MODERATOR]: [
    // Can view most things and manage appointments/students but no system settings
    PERMISSIONS.VIEW_AVAILABILITY,
    PERMISSIONS.VIEW_APPOINTMENTS,
    PERMISSIONS.MANAGE_APPOINTMENTS,
    PERMISSIONS.VIEW_STUDENTS,
    PERMISSIONS.MANAGE_STUDENTS,
    PERMISSIONS.VIEW_MAILING_LIST,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_ACTIVITY_LOGS
  ]
};

// Helper functions
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  const rolePermissions = ROLE_PERMISSIONS[userRole];
  return rolePermissions ? rolePermissions.includes(permission) : false;
};

export const hasAnyPermission = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  return permissions.some(permission => hasPermission(userRole, permission));
};

export const hasAllPermissions = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  return permissions.every(permission => hasPermission(userRole, permission));
};

export const getRoleDisplayName = (role) => {
  switch (role) {
    case ROLES.ADMIN:
      return 'Yönetici (Admin)';
    case ROLES.MARKETING:
      return 'Pazarlama (Marketing)';
    case ROLES.MODERATOR:
      return 'Moderatör (Moderator)';
    default:
      return 'Bilinmeyen Rol';
  }
};

export const getRoleDescription = (role) => {
  switch (role) {
    case ROLES.ADMIN:
      return 'Tüm özelliklere tam erişim';
    case ROLES.MARKETING:
      return 'Analitikler, mailing list ve pazarlama ayarları';
    case ROLES.MODERATOR:
      return 'Randevu ve öğrenci yönetimi, analitik görüntüleme';
    default:
      return 'Rol tanımı bulunamadı';
  }
};

export const getAvailableRoles = () => {
  return Object.values(ROLES);
};

export const isValidRole = (role) => {
  return Object.values(ROLES).includes(role);
};

// Permission checking for UI components
export const canAccessTab = (userRole, tabId) => {
  switch (tabId) {
    case 'availability':
      return hasPermission(userRole, PERMISSIONS.VIEW_AVAILABILITY);
    case 'appointments':
      return hasPermission(userRole, PERMISSIONS.VIEW_APPOINTMENTS);
    case 'incomplete':
      return hasPermission(userRole, PERMISSIONS.VIEW_APPOINTMENTS);
    case 'completed':
      return hasPermission(userRole, PERMISSIONS.VIEW_APPOINTMENTS);
    case 'students':
      return hasPermission(userRole, PERMISSIONS.VIEW_STUDENTS);
    case 'mailing':
      return hasPermission(userRole, PERMISSIONS.VIEW_MAILING_LIST);
    case 'activity':
      return hasPermission(userRole, PERMISSIONS.VIEW_ACTIVITY_LOGS);
    case 'fomo':
      return hasPermission(userRole, PERMISSIONS.MANAGE_FOMO_SETTINGS);
    case 'accounting':
      return hasPermission(userRole, PERMISSIONS.VIEW_ACCOUNTING);
    case 'analytics':
      return hasPermission(userRole, PERMISSIONS.VIEW_ANALYTICS);
    case 'traffic':
      return hasPermission(userRole, PERMISSIONS.VIEW_ANALYTICS);
    case 'users':
      return hasPermission(userRole, PERMISSIONS.MANAGE_USERS);
    default:
      return false;
  }
};